import { useMemo, useState, CSSProperties } from 'react';
import { useRecoilValue } from 'recoil';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
  MoviefoneAnalysis,
  SearchCandidate,
  StructureRequest,
} from 'gen/stream-api/requests/types.gen';
import {
  formatMaxProfit,
  formatMaxRisk,
  netCash,
  rewardToRiskRatio,
  RiskTier,
  tierColor,
  tierFromRiskScore,
} from 'util/moviefoneFormat';

import range from 'lodash/range';
import {
  moviefoneTradeFiltersState,
  MoviefoneRiskTolerance,
} from 'states/moviefone';
import { LOW_RISK_MAX, MEDIUM_RISK_MAX } from 'util/moviefoneFormat';

const TABLE_TO_INLINE_LABELS_BREAK_EX = 60;

const TRADE_COLUMNS: {
  label: string;
  accessor: (s: SearchCandidate) => string;
  cellBoxSx?: (s: SearchCandidate) => Partial<CSSProperties>;
}[] = [
  {
    label: 'Structure',
    accessor: (s) => s.family_label!,
    cellBoxSx: (_s) => ({
      textAlign: 'left',
    }),
  },
  {
    label: 'Net Debit/Credit',
    accessor: (s) => {
      const cash = netCash(s);
      return `${cash.value} ${cash.label === 'Net Debit' ? 'debit' : 'credit'}`;
    },
    cellBoxSx: (_s) => ({
      textAlign: 'right',
      whiteSpace: 'nowrap',
    }),
  },
  {
    label: 'Max Profit',
    accessor: (s) => formatMaxProfit(s),
    cellBoxSx: (_s) => ({
      textAlign: 'right',
    }),
  },
  {
    label: 'Max Loss',
    accessor: (s) => formatMaxRisk(s),
    cellBoxSx: (_s) => ({
      textAlign: 'right',
    }),
  },
  {
    label: 'Probability of Profit',
    accessor: (s) =>
      s.final_score != null && s.pop != null
        ? new Intl.NumberFormat('en', { maximumSignificantDigits: 2 }).format(
            s.pop,
          ) + '%'
        : '',
  },
  {
    label: 'Risk-Reward Ratio',
    accessor: (s) => {
      const ratio = rewardToRiskRatio(s);
      return ratio != null
        ? new Intl.NumberFormat('en', { maximumSignificantDigits: 2 }).format(
            ratio,
          )
        : 'N/A';
    },
    cellBoxSx: (_s) => ({
      textAlign: 'center',
    }),
  },
];

interface TradeCard {
  /** Null when the SearchCandidate carries no risk_score to classify it by. */
  tier: RiskTier | null;
  searchCandidate: SearchCandidate;
}

/** Pick up to one SearchCandidate per risk tier, preferring the highest-scoring in each. */
function pickByRiskTier(strategies: SearchCandidate[]): TradeCard[] {
  const byTier = new Map<RiskTier, SearchCandidate>();
  for (const s of strategies) {
    if (s.risk_score == null) {
      continue;
    }
    const tier = tierFromRiskScore(s.risk_score.total);
    const existing = byTier.get(tier);
    if (existing == null || s.final_score! > existing.final_score!) {
      byTier.set(tier, s);
    }
  }
  const ordered: RiskTier[] = ['low', 'medium', 'high'];
  return ordered
    .filter((tier) => byTier.has(tier))
    .map((tier) => ({ tier, searchCandidate: byTier.get(tier)! }));
}

/**
 * Pick trade cards to show, preferring real risk-tier classification.
 * Falls back to the top-scoring strategies (untiered) when the response
 * doesn't carry risk_score, so real recommendations still surface.
 */
function pickTradeCards(
  unfileredStrategies: SearchCandidate[],
  riskTolerance: MoviefoneRiskTolerance,
  min_pnl_ratio: number,
  max_pnl_ratio: number,
): TradeCard[] {
  const strategies = unfileredStrategies
    .filter((s) => {
      if (s.max_risk == null) {
        return false;
      }
      const maxPnl = (s.max_profit ?? Infinity) / s.max_risk;
      return (
        (riskTolerance == 'high' ||
          (s.risk_score?.total != null &&
            s.risk_score?.total <
              (riskTolerance == 'low' ? LOW_RISK_MAX : MEDIUM_RISK_MAX))) &&
        min_pnl_ratio <= maxPnl &&
        maxPnl <= (max_pnl_ratio ?? Infinity)
      );
    })
    .sort(
      (a, b) =>
        (a.risk_score?.total ?? Infinity) - (b.risk_score?.total ?? Infinity),
    );

  if (strategies.length == 0) {
    return [];
  }

  if (riskTolerance != 'high') {
    const maxIdx = strategies.length - 1;
    const lo = Math.floor(maxIdx * 0.05);
    const med = Math.floor(maxIdx * 0.5);
    const hi = Math.ceil(maxIdx * 0.95);

    const result = [
      {
        tier:
          (strategies[lo].risk_score?.total ?? Infinity) < LOW_RISK_MAX
            ? 'low'
            : 'medium',
        searchCandidate: strategies[lo],
      },
    ];

    if (med > lo) {
      result.push({
        tier:
          (strategies[med].risk_score?.total ?? Infinity) < LOW_RISK_MAX
            ? 'low'
            : 'medium',
        searchCandidate: strategies[med],
      });
    }

    if (hi > med) {
      result.push({
        tier:
          (strategies[hi].risk_score?.total ?? Infinity) < LOW_RISK_MAX
            ? 'low'
            : 'medium',
        searchCandidate: strategies[hi],
      });
    }

    return result as unknown as TradeCard[];
  }

  const tiered = pickByRiskTier(strategies);
  if (tiered.length > 0) {
    return tiered;
  }
  return [...strategies]
    .sort((a, b) => b.final_score! - a.final_score!)
    .slice(0, 3)
    .map((searchCandidate) => ({ tier: null, searchCandidate }));
}

const CARD_WIDTH = 300;
const CARD_GAP = 12;
const ROW_PADDING = 40;
const rowWidth = (count: number) =>
  count * CARD_WIDTH + (count - 1) * CARD_GAP + ROW_PADDING;

interface MoviefoneNTradesProps {
  moviefoneAnalysis?: MoviefoneAnalysis | null;
  tradeCriteria?: StructureRequest;
  onSelectTrade?: (
    moviefoneAnalysis: MoviefoneAnalysis,
    searchCandidate: SearchCandidate,
  ) => void;
  onSelectCompareMode?: (
    moviefoneAnalysis: MoviefoneAnalysis,
    strategies: SearchCandidate[],
  ) => void;
}

const MoviefoneNTrades = ({
  moviefoneAnalysis,
  tradeCriteria,
  onSelectTrade,
  onSelectCompareMode,
}: MoviefoneNTradesProps) => {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const min_pnl_ratio = tradeCriteria?.bounds?.min_reward_to_risk ?? 0;
  const { riskTolerance, max_pnl_ratio } = useRecoilValue(
    moviefoneTradeFiltersState,
  );

  const cards = useMemo(
    () =>
      pickTradeCards(
        moviefoneAnalysis?.structure_search?.candidates ?? [],
        riskTolerance,
        min_pnl_ratio,
        max_pnl_ratio,
      ),
    [moviefoneAnalysis, riskTolerance, min_pnl_ratio, max_pnl_ratio],
  );

  if (moviefoneAnalysis?.structure_search?.candidates == null) {
    return null;
  }

  // Not one of `cards` - a trailing sentinel index for the Compare Options tile.
  const compareIndex = cards.length;
  const selectCompareMode = () => {
    setSelectedIndex(compareIndex);
    onSelectCompareMode?.(
      moviefoneAnalysis,
      cards.map((c) => c.searchCandidate),
    );
  };

  return (
    <Box sx={{ padding: '0 0 16px', containerType: 'inline-size' }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: theme.palette.text.primary,
          textAlign: 'center',
        }}
      >
        {moviefoneAnalysis.symbol} Trades
      </Typography>
      {cards.length == 0 && (
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: theme.palette.text.primary,
            textAlign: 'center',
          }}
        >
          No trades matching your criteria were found.
        </Typography>
      )}
      {cards.length > 0 && (
        <>
          <Box sx={{ marginBottom: '10ex' }}>
            <Box
              sx={{
                [`@container (min-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex)`]:
                  {
                    'grid-template-columns': range(TRADE_COLUMNS.length)
                      .map((idx) => (idx === 0 ? '2.5fr' : '1fr'))
                      .join(' '),
                    display: 'grid',
                    rowGap: '5px',

                    // Each data row is a run of TRADE_COLUMNS.length flat grid
                    // siblings (no wrapper element, so column alignment across rows
                    // is untouched). These classes stitch that run into the look of
                    // a single bordered row, colored per-cell via the `--row-color`
                    // custom property.
                    '& .trade-row-cell': {
                      borderTop: '1px solid var(--row-color)',
                      borderBottom: '1px solid var(--row-color)',
                    },
                    '& .trade-row-cell--first': {
                      borderLeft: '1px solid var(--row-color)',
                      borderTopLeftRadius: '4px',
                      borderBottomLeftRadius: '4px',
                    },
                    '& .trade-row-cell--last': {
                      borderRight: '1px solid var(--row-color)',
                      borderTopRightRadius: '4px',
                      borderBottomRightRadius: '4px',
                    },
                  },
                [`@container (max-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex)`]:
                  {
                    display: 'none',
                  },

                padding: '20px',
                margin: '0 auto',
                maxWidth: `${TABLE_TO_INLINE_LABELS_BREAK_EX * 1.6}ex`,
                borderRadius: '4px',
                backgroundColor: theme.palette.background.paper,
              }}
            >
              {TRADE_COLUMNS.map((c) => (
                <Box
                  sx={{
                    textAlign: 'center',
                  }}
                  key={c.label}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600, fontSize: '10px' }}
                  >
                    {c.label.toUpperCase()}
                  </Typography>
                  {cards.length == 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, fontWeight: 600, fontSize: '10px' }}
                    >
                      No trades matching your criteria were found.
                    </Typography>
                  )}
                </Box>
              ))}
              {cards.map(({ tier, searchCandidate: s }, index) =>
                TRADE_COLUMNS.map((c, idx) => (
                  <Box
                    key={`${index}_${idx}`}
                    className={[
                      'trade-row-cell',
                      idx === 0 && 'trade-row-cell--first',
                      idx === TRADE_COLUMNS.length - 1 &&
                        'trade-row-cell--last',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setSelectedIndex(index);
                      onSelectTrade?.(moviefoneAnalysis, s);
                    }}
                    style={
                      {
                        '--row-color': tierColor(tier, theme),
                      } as CSSProperties
                    }
                    sx={{
                      cursor: 'pointer',
                      backgroundColor:
                        index == selectedIndex
                          ? theme.palette.mutedSgGreen
                          : theme.palette.background.paper,
                      padding: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        [`@container (min-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex )`]:
                          {
                            display: 'none',
                          },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600, fontSize: '10px' }}
                      >
                        {c.label.toUpperCase()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        textAlign: 'center',
                        ...(c.cellBoxSx?.(s) ?? {}),
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                        }}
                      >
                        {c.accessor(s)}
                      </Typography>
                    </Box>
                  </Box>
                )),
              )}

              <Box
                onClick={selectCompareMode}
                sx={{
                  gridColumn: '1 / -1',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  marginTop: '4px',
                  padding: '10px',
                  borderRadius: '4px',
                  border: `1px dashed ${alpha(theme.palette.text.secondary, 0.5)}`,
                  backgroundColor:
                    selectedIndex === compareIndex
                      ? theme.palette.mutedSgGreen
                      : theme.palette.background.paper,
                }}
              >
                <CompareArrowsIcon
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: '0.5px' }}
                >
                  Compare Options
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                [`@container (min-width: ${TABLE_TO_INLINE_LABELS_BREAK_EX}ex)`]:
                  {
                    display: 'none',
                  },
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: `${CARD_GAP}px`,
                padding: '20px',
                margin: '0 auto',
                borderRadius: '4px',
                backgroundColor: theme.palette.background.paper,
                // width is capped to the container's available space once
                // flex-wrap kicks in, so `fit-content` can't shrink to the
                // width of the row that actually rendered. Set it explicitly
                // per breakpoint, capped at the number of cards we actually have.
                width: `${rowWidth(1)}px`,
                ...(cards.length + 1 >= 2 && {
                  '@container (min-width: 652px)': {
                    width: `${rowWidth(2)}px`,
                  },
                }),
                ...(cards.length + 1 >= 3 && {
                  '@container (min-width: 964px)': {
                    width: `${rowWidth(3)}px`,
                  },
                }),
                ...(cards.length + 1 >= 4 && {
                  '@container (min-width: 1276px)': {
                    width: `${rowWidth(4)}px`,
                  },
                }),
              }}
            >
              {cards.map(({ tier, searchCandidate: s }, index) => {
                const isSelected = index === selectedIndex;
                const color = tierColor(tier, theme);
                const rewardRiskRatio = rewardToRiskRatio(s);
                const cash = netCash(s);
                return (
                  <Box
                    key={`${tier ?? 'untiered'}-${s.family_label}-${index}`}
                    onClick={() => {
                      setSelectedIndex(index);
                      onSelectTrade?.(moviefoneAnalysis, s);
                    }}
                    sx={{
                      width: `${CARD_WIDTH}px`,
                      padding: '16px',
                      borderRadius: '8px',
                      border: `1px solid ${color}`,
                      backgroundColor: isSelected
                        ? theme.palette.mutedSgGreen
                        : theme.palette.background.paper,
                      cursor: 'pointer',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        mt: 0.5,
                        mb: 1,
                      }}
                    >
                      {s.family_label}&nbsp;&nbsp;&nbsp;
                      {cash.label} {cash.value}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        mb: 1,
                        gap: '8px',
                      }}
                    ></Box>
                    <Box
                      sx={{
                        borderBottom: `1px dashed ${theme.palette.divider}`,
                        mb: 1.5,
                      }}
                    />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '8px',
                        mb: 1.5,
                      }}
                    >
                      {[
                        { label: 'Max Profit', value: formatMaxProfit(s) },
                        { label: 'Max Loss', value: formatMaxRisk(s) },
                        {
                          label: 'POP',
                          value:
                            s.final_score != null && s.pop != null
                              ? new Intl.NumberFormat('en', {
                                  maximumSignificantDigits: 2,
                                }).format(s.pop) + '%'
                              : '',
                        },
                        {
                          label: 'Reward / Risk',
                          value:
                            rewardRiskRatio != null
                              ? new Intl.NumberFormat('en', {
                                  maximumSignificantDigits: 2,
                                }).format(rewardRiskRatio)
                              : 'N/A',
                        },
                      ].map((stat) => (
                        <Box key={stat.label}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontSize: '9.5px',
                              textTransform: 'uppercase',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {stat.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {stat.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}

              <Box
                onClick={selectCompareMode}
                sx={{
                  width: `${CARD_WIDTH}px`,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px dashed ${alpha(theme.palette.text.secondary, 0.5)}`,
                  backgroundColor:
                    selectedIndex === compareIndex
                      ? theme.palette.mutedSgGreen
                      : theme.palette.background.paper,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <CompareArrowsIcon
                  sx={{ color: theme.palette.text.secondary }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  Compare Options
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default MoviefoneNTrades;
